import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { contentService, Page, Article, ArticleCategory, CreatePageDto, UpdatePageDto, CreateArticleDto, UpdateArticleDto, CreateArticleCategoryDto, UpdateArticleCategoryDto, PageQueryDto, ArticleQueryDto } from '../services/content.service';

interface UsePagesReturn {
  pages: Page[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  refetch: () => void;
  createPage: (data: CreatePageDto) => Promise<Page>;
  updatePage: (id: string, data: UpdatePageDto) => Promise<Page>;
  togglePageState: (id: string) => Promise<void>;
  deletePages: (ids: string[]) => Promise<void>;
}

export const usePages = (initialFilters: PageQueryDto = {}): UsePagesReturn => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters] = useState<PageQueryDto>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contentService.pages.getAll(filters);
      setPages(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch pages'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const createPage = async (data: CreatePageDto): Promise<Page> => {
    const page = await contentService.pages.create(data);
    await fetchPages();
    return page;
  };

  const updatePage = async (id: string, data: UpdatePageDto): Promise<Page> => {
    const page = await contentService.pages.update(id, data);
    setPages((prev) => prev.map((p) => (p.page_id === id ? { ...p, ...page } : p)));
    return page;
  };

  const togglePageState = async (id: string): Promise<void> => {
    const page = await contentService.pages.toggleState(id);
    setPages((prev) => prev.map((p) => (p.page_id === id ? { ...p, ...page } : p)));
  };

  const deletePages = async (ids: string[]): Promise<void> => {
    await contentService.pages.deleteMany(ids);
    setPages((prev) => prev.filter((p) => !ids.includes(p.page_id)));
    setMeta((prev) => ({ ...prev, total: prev.total - ids.length }));
  };

  return {
    pages,
    loading,
    error,
    meta,
    refetch: fetchPages,
    createPage,
    updatePage,
    togglePageState,
    deletePages,
  };
};

interface UseArticlesReturn {
  articles: Article[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: ArticleQueryDto;
  setFilters: Dispatch<SetStateAction<ArticleQueryDto>>;
  refetch: () => void;
  createArticle: (data: CreateArticleDto) => Promise<Article>;
  updateArticle: (id: string, data: UpdateArticleDto) => Promise<Article>;
  toggleArticleState: (id: string) => Promise<void>;
  deleteArticles: (ids: string[]) => Promise<void>;
}

export const useArticles = (initialFilters: ArticleQueryDto = {}): UseArticlesReturn => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<ArticleQueryDto>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contentService.articles.getAll(filters);
      setArticles(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch articles'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const createArticle = async (data: CreateArticleDto): Promise<Article> => {
    const article = await contentService.articles.create(data);
    await fetchArticles();
    return article;
  };

  const updateArticle = async (id: string, data: UpdateArticleDto): Promise<Article> => {
    const article = await contentService.articles.update(id, data);
    setArticles((prev) => prev.map((a) => (a.article_id === id ? { ...a, ...article } : a)));
    return article;
  };

  const toggleArticleState = async (id: string): Promise<void> => {
    const article = await contentService.articles.toggleState(id);
    setArticles((prev) => prev.map((a) => (a.article_id === id ? { ...a, ...article } : a)));
  };

  const deleteArticles = async (ids: string[]): Promise<void> => {
    await contentService.articles.deleteMany(ids);
    setArticles((prev) => prev.filter((a) => !ids.includes(a.article_id)));
    setMeta((prev) => ({ ...prev, total: prev.total - ids.length }));
  };

  return {
    articles,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchArticles,
    createArticle,
    updateArticle,
    toggleArticleState,
    deleteArticles,
  };
};

interface UseArticleCategoriesReturn {
  categories: ArticleCategory[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  setTenantFilter: (tenant_id: string) => void;
  createCategory: (data: CreateArticleCategoryDto) => Promise<ArticleCategory>;
  updateCategory: (id: string, data: UpdateArticleCategoryDto) => Promise<ArticleCategory>;
  deleteCategories: (ids: string[]) => Promise<void>;
  updateOrdering: (categories: Array<{ _id: string; ordering: number }>) => Promise<void>;
}

export const useArticleCategories = (initialQuery: { tenant_id?: string } = {}): UseArticleCategoriesReturn => {
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [query, setQuery] = useState(initialQuery);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contentService.articleCategories.getAll(query);
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = async (data: CreateArticleCategoryDto): Promise<ArticleCategory> => {
    const category = await contentService.articleCategories.create(data);
    await fetchCategories();
    return category;
  };

  const updateCategory = async (id: string, data: UpdateArticleCategoryDto): Promise<ArticleCategory> => {
    const category = await contentService.articleCategories.update(id, data);
    await fetchCategories();
    return category;
  };

  const deleteCategories = async (ids: string[]): Promise<void> => {
    await contentService.articleCategories.deleteMany(ids);
    setCategories((prev) => prev.filter((c) => !ids.includes(c.category_id || c._id)));
  };

  const updateOrdering = async (cats: Array<{ _id: string; ordering: number }>): Promise<void> => {
    await contentService.articleCategories.updateOrdering(cats);
    await fetchCategories();
  };

  const setTenantFilter = (tenant_id: string) => {
    setQuery((prev) => ({ ...prev, tenant_id: tenant_id || undefined }));
  };

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    setTenantFilter,
    createCategory,
    updateCategory,
    deleteCategories,
    updateOrdering,
  };
};